"use client";

import React from "react";
import { Calendar, User, ArrowRight, Tag, ChevronLeft } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i} data-index={i} className="animate-pulse border-border shadow-sm">
            <div className="h-48 w-full bg-muted rounded-md" />
            <CardHeader>
              <div className="h-6 w-3/4 bg-muted rounded-md" />
            </CardHeader>
            <CardContent>
              <div className="h-4 w-full bg-muted rounded-md mb-2" />
              <div className="h-4 w-2/3 bg-muted rounded-md" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => (
        <Card key={post.id} data-index={i} className="flex flex-col overflow-hidden border-border shadow-md hover:shadow-lg transition-shadow duration-300 rounded-md">
          {post.featuredImage && (
            <Link to={post.link || "#"} className="block overflow-hidden aspect-video">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                loading="lazy"
              />
            </Link>
          )}
          <CardHeader className="flex-grow">
            <CardTitle className="text-xl font-default font-bold line-clamp-2 text-foreground">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
            {post.excerpt && (
              <CardDescription className="line-clamp-3 mt-2 text-muted-foreground font-sans">
                <span dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              </CardDescription>
            )}
          </CardHeader>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="text-sm font-bold text-primary hover:text-primary/80 inline-flex items-center gap-1 uppercase tracking-wider">
              Read Legal Insight <ArrowRight className="size-4" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto animate-pulse">
        <div className="h-10 w-3/4 bg-muted rounded-md mb-6" />
        <div className="flex gap-4 mb-8">
          <div className="h-6 w-32 bg-muted rounded-md" />
          <div className="h-6 w-32 bg-muted rounded-md" />
        </div>
        <div className="h-[400px] w-full bg-muted rounded-md mb-8" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted rounded-md" />
          <div className="h-4 w-full bg-muted rounded-md" />
          <div className="h-4 w-5/6 bg-muted rounded-md" />
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <h1 className="text-4xl font-default font-bold mb-4 text-foreground">Article Not Found</h1>
        <p className="text-muted-foreground mb-8 text-lg font-sans">The legal resource you are looking for is currently unavailable.</p>
        <Link to="/HomePage">
          <Button variant="default" size="lg" className="rounded-md font-bold uppercase tracking-wide">
            <ChevronLeft className="mr-2 size-4" /> Return to Home
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <header className="mb-10 text-center md:text-left">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4 justify-center md:justify-start">
            {post.categories.map((cat, i) => (
              <Link key={cat.id} data-index={i} to={cat.link || "#"}>
                <span className="inline-flex items-center px-4 py-1 rounded-md bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        )}
        
        <h1 className="text-4xl md:text-6xl font-default font-bold text-foreground leading-tight mb-6 tracking-tight">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-muted-foreground text-sm font-sans">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-10 border-2 border-primary/20 rounded-md">
                {post.author.avatar && post.author.avatar["48"] ? (
                  <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
                ) : (
                  <AvatarFallback className="bg-muted text-primary rounded-md">
                    <User className="size-5" />
                  </AvatarFallback>
                )}
              </Avatar>
              <Link to={post.author.link || "#"} className="font-bold text-foreground hover:text-primary transition-colors uppercase tracking-tighter">
                {post.author.name}
              </Link>
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2 font-medium">
              <Calendar className="size-4 text-primary" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <figure className="mb-12 rounded-md overflow-hidden shadow-xl border border-border">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto max-h-[650px] object-cover"
          />
        </figure>
      )}

      {}
      <div 
        className="prose prose-lg max-w-none text-foreground font-sans [&>p]:mb-8 [&>p]:leading-relaxed [&>p]:text-lg [&>h2]:text-4xl [&>h2]:font-default [&>h2]:font-bold [&>h2]:mt-16 [&>h2]:mb-8 [&>h2]:text-foreground [&>h2]:border-b [&>h2]:border-border [&>h2]:pb-4 [&>h3]:text-2xl [&>h3]:font-default [&>h3]:font-bold [&>h3]:mt-10 [&>h3]:mb-6 [&>h3]:text-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-8 [&>ul>li]:mb-3 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-8 [&>ol>li]:mb-3 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-8 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:my-12 [&>blockquote]:bg-muted/20 [&>blockquote]:py-4 [&>a]:text-primary [&>a]:font-bold [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80 [&>img]:rounded-md [&>img]:shadow-md [&>img]:my-12"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-16 pt-10 border-t border-border flex flex-wrap items-center gap-3">
          <Tag className="size-5 text-primary" />
          <span className="text-sm font-bold text-foreground uppercase tracking-widest mr-2">Legal Tags:</span>
          {post.tags.map((tag, i) => (
            <Link key={tag.id} data-index={i} to={tag.link || "#"}>
              <span className="inline-flex items-center px-4 py-1 rounded-md bg-muted text-muted-foreground text-xs font-bold uppercase tracking-tighter hover:bg-primary hover:text-primary-foreground transition-all">
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      {}
      {post.author && (
        <Card className="mt-16 bg-card border-border shadow-lg rounded-md">
          <CardContent className="p-8 sm:p-12 flex flex-col sm:flex-row items-center sm:items-start gap-8">
            <Avatar className="size-24 sm:size-32 border-4 border-background shadow-md rounded-md">
              {post.author.avatar && post.author.avatar["96"] ? (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              ) : (
                <AvatarFallback className="bg-primary text-primary-foreground text-3xl font-bold rounded-md">
                  {post.author.name.charAt(0)}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="text-center sm:text-left flex-1">
              <h3 className="text-2xl font-default font-bold text-foreground mb-3">
                Legal Counsel: {post.author.name}
              </h3>
              <p className="text-muted-foreground mb-6 font-sans text-lg leading-relaxed">
                Sterling Law Associates contributor specializing in complex litigation and legal strategy. Dedicated to providing clarity in the legal landscape.
              </p>
              <Link to={post.author.link || "#"}>
                <Button variant="outline" size="md" className="rounded-md font-bold uppercase tracking-wide border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                  View Counsel Profile
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && wvcClient ? wvcClient.getCurrentWPQueryParams() : { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  
  const related_wp_query = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <section data-section-id="244817" id="single-post-template-section" className="w-full bg-background py-20 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="max-w-4xl mx-auto mt-24 pt-20 border-t-2 border-border">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground tracking-tight">
              Further Legal Insights
            </h2>
            <Link to="/LegalInsightsPage">
              <Button variant="ghost" className="hidden sm:flex font-bold uppercase tracking-widest text-primary hover:bg-primary/10">
                Explore All <ArrowRight className="ml-2 size-4" />
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsConsumer />
          </PostsProvider>
          
          <div className="mt-12 text-center sm:hidden">
            <Link to="/LegalInsightsPage">
              <Button variant="outline" className="w-full rounded-md font-bold uppercase tracking-wide border-primary text-primary">
                View All Insights
              </Button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}